import { FC } from 'react';
import {
  NavigationPageItemDto,
  OneColumnGroupDto,
  PageContentError,
  TabDto,
  TabGroupDto,
  TwoColumnGroupDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  OneColumnGroup,
  Tab,
  TabGroup,
  TwoColumnGroup,
} from '@asset-metrix/portal-ui';

import { Table } from '../table/Table';
import { TableauChart } from '../tableauchart/TableauChart';
import { VegaChart } from '../vegachart/VegaChart';

import { PageContentError as PageContentErrorRenderer } from './PageContentError';

function WidgetContent({
  pageNode,
  pageId,
  instanceId,
  child_key,
}: Omit<PageContentRendererProps<WidgetDto>, 'Render'>) {
  const entity = pageNode.entity;

  if (entity.type === 'table') {
    return (
      <Table
        key={child_key}
        pageId={pageId}
        instanceId={instanceId}
        {...pageNode}
        entity={entity}
      />
    );
  }
  if (entity.type === 'tableauChart') {
    return (
      <TableauChart
        key={child_key}
        pageId={pageId}
        {...pageNode}
        entity={entity}
        instanceId={instanceId}
      />
    );
  }
  if (entity.type === 'vegaChart') {
    return (
      <VegaChart
        key={child_key}
        {...pageNode}
        entity={entity}
        pageId={pageId}
        instanceId={instanceId}
      />
    );
  }
  if (entity.type === 'textFromTable') {
    return <p>{entity.config.text}</p>;
  }

  throw new Error('Widget entity unknown');
}

function SingleColumGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<OneColumnGroupDto>) {
  return (
    <OneColumnGroup key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => (
          <Render
            key={`${child_key}-${index}`}
            child_key={`${child_key}-${index}`}
            pageNode={content}
            Render={Render}
            pageId={pageId}
            instanceId={instanceId}
          />
        ))}
    </OneColumnGroup>
  );
}

function TwoColumGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TwoColumnGroupDto>) {
  return (
    <TwoColumnGroup key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => (
          <Render
            key={`${child_key}-${index}`}
            child_key={`${child_key}-${index}`}
            pageNode={content}
            Render={Render}
            pageId={pageId}
            instanceId={instanceId}
          />
        ))}
    </TwoColumnGroup>
  );
}

function TabGroupContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TabGroupDto>) {
  return (
    <TabGroup entity={pageNode} key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => (
          <Render
            key={`${child_key}-${index}`}
            child_key={`${child_key}-${index}`}
            pageNode={content}
            Render={Render}
            pageId={pageId}
            instanceId={instanceId}
          />
        ))}
    </TabGroup>
  );
}

function TabContent({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps<TabDto>) {
  return (
    <Tab entity={pageNode} key={child_key}>
      {pageNode.contents &&
        pageNode.contents.map((content, index) => (
          <Render
            key={`${child_key}-${index}`}
            child_key={`${child_key}-${index}`}
            pageNode={content}
            Render={Render}
            pageId={pageId}
            instanceId={instanceId}
          />
        ))}
    </Tab>
  );
}

export type PageContentRendererProps<
  T =
    | PageContentError
    | WidgetDto
    | OneColumnGroupDto
    | TwoColumnGroupDto
    | TabGroupDto
    | TabDto,
> = {
  pageNode: T;
  Render: FC<PageContentRendererProps>;
  pageId: NavigationPageItemDto['pageId'];
  instanceId: string;
  child_key?: string;
};

export function PageContentRenderer({
  pageNode,
  Render,
  pageId,
  instanceId,
  child_key,
}: PageContentRendererProps) {
  switch (pageNode.type) {
    case 'widget':
      return WidgetContent({ pageNode, pageId, instanceId, child_key });
    case 'singleColumnGroup':
      return SingleColumGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'twoColumnGroup':
      return TwoColumGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'tabGroup':
      return TabGroupContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'tab':
      return TabContent({
        pageNode,
        Render,
        pageId,
        instanceId,
        child_key,
      });
    case 'error':
      console.error(pageNode.message);
      return <PageContentErrorRenderer message={pageNode.message} />;
    default:
      return <div style={{ color: 'red' }}>Unknown page content node</div>;
  }
}
